import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { PORTFOLIO, getPortfolioBySlug } from "@/data/portfolio";
import { SERVICES } from "@/data/services";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import RelatedContent from "@/components/RelatedContent";
import CTABlock from "@/components/CTABlock";
import PortfolioCover from "@/components/PortfolioCover";

const ALL = "all";

export const PortfolioIndex = () => {
  useSeo(SEO.portfolio);
  const [type, setType] = useState(ALL);
  const [category, setCategory] = useState(ALL);

  const types = useMemo(
    () => Array.from(new Set(PORTFOLIO.map((p) => p.type))).sort(),
    []
  );
  const categories = useMemo(
    () => Array.from(new Set(PORTFOLIO.map((p) => p.category))).sort(),
    []
  );

  const filtered = useMemo(
    () =>
      PORTFOLIO.filter(
        (p) =>
          (type === ALL || p.type === type) &&
          (category === ALL || p.category === category)
      ),
    [type, category]
  );

  return (
    <main data-testid="portfolio-page" className="bg-black text-white">
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Portfolio" }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
            Real work. <span className="k-yellow">Named clients. Named problems.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">
            Client projects, Kostaja-owned brands, and internal creative development. Presented under the real client name where written permission is on file. Where final imagery has not yet been released, projects ship with a branded placeholder rather than stock photography.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-black border-b border-white/10 sticky top-16 lg:top-20 z-30" data-testid="portfolio-filters">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          <Overline color="yellow" className="mr-3">Filter</Overline>
          <button
            data-testid="portfolio-type-all"
            onClick={() => setType(ALL)}
            className={`px-3.5 py-2 border-2 text-[11px] uppercase tracking-[0.18em] font-semibold ${
              type === ALL ? "bg-[#FFC600] text-black border-[#FFC600]" : "border-white/25 text-white hover:border-[#FFC600] hover:text-[#FFC600]"
            }`}
          >
            All types
          </button>
          {types.map((t) => (
            <button
              key={t}
              data-testid={`portfolio-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setType(t)}
              className={`px-3.5 py-2 border-2 text-[11px] uppercase tracking-[0.18em] font-semibold ${
                type === t ? "bg-[#FFC600] text-black border-[#FFC600]" : "border-white/25 text-white hover:border-[#FFC600] hover:text-[#FFC600]"
              }`}
            >
              {t}
            </button>
          ))}
          <span className="w-full lg:w-px lg:h-6 lg:bg-white/15 lg:mx-2" />
          <select
            data-testid="portfolio-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 border-2 border-white/25 bg-black text-white px-3 text-sm"
            aria-label="Filter by category"
          >
            <option value={ALL}>All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {(type !== ALL || category !== ALL) && (
            <button
              data-testid="portfolio-clear"
              onClick={() => { setType(ALL); setCategory(ALL); }}
              className="text-sm font-semibold underline underline-offset-4 text-white/70 hover:text-[#FFC600] ml-2"
            >
              Clear
            </button>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="bg-black">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16">
          {filtered.length === 0 ? (
            <div data-testid="portfolio-empty" className="border border-white/15 p-10 text-center">
              <h3 className="text-2xl font-black tracking-tighter">No projects match those filters.</h3>
              <div className="mt-6 flex justify-center gap-3">
                <SecondaryButton dark onClick={() => { setType(ALL); setCategory(ALL); }} to="/portfolio">View all work</SecondaryButton>
                <PrimaryButton to="/contact">Tell us what you need</PrimaryButton>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8">
              {filtered.map((p, idx) => (
                <Link
                  key={p.slug}
                  to={`/portfolio/${p.slug}`}
                  data-testid={`portfolio-item-${p.slug}`}
                  className={`group block border border-white/15 hover:border-[#FFC600] transition-colors overflow-hidden bg-black ${
                    idx % 3 === 0 ? "lg:col-span-7" : "lg:col-span-5"
                  }`}
                >
                  <div className="aspect-[16/10] bg-black overflow-hidden">
                    <PortfolioCover item={p} accent={idx % 2 === 0 ? "yellow" : "outline"} />
                  </div>
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFC600] font-bold">{p.type}</span>
                      <span className="text-white/30">·</span>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">{p.category}</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-black mt-2 tracking-tighter leading-tight text-white group-hover:text-[#FFC600] transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-white/70 mt-2 leading-relaxed">{p.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABlock variant="default" />
    </main>
  );
};

export const PortfolioDetail = () => {
  const { slug } = useParams();
  const item = getPortfolioBySlug(slug);
  useSeo({
    title: item ? `${item.title} | Portfolio | Kostaja` : "Portfolio | Kostaja",
    description: item?.summary || "",
  });

  if (!item) {
    return (
      <main className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <h1 className="text-5xl font-black tracking-tighter">Project not found.</h1>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton dark to="/portfolio">Portfolio</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }

  const services = SERVICES.filter((s) => item.relatedServices?.includes(s.slug));
  const hasLogo = Boolean(item.logo);
  const brandBg = item.logoBg || "#0B0B0B";

  return (
    <main data-testid="portfolio-detail" className="bg-black text-white">
      {/* 1 · EDITORIAL POSTER HERO (full-bleed brand plate) */}
      <section
        data-testid="portfolio-poster"
        className="relative w-full overflow-hidden border-b border-white/10"
        style={{ background: brandBg }}
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-6 lg:pt-8">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/portfolio", label: "Portfolio" }, { label: item.title }]} />
        </div>

        <div className="relative min-h-[70vh] lg:min-h-[86vh] flex items-center justify-center py-16 lg:py-24">
          {hasLogo ? (
            <img
              src={item.logo}
              alt={`${item.client || item.title} logo`}
              loading="eager"
              className="max-w-[62%] lg:max-w-[46%] max-h-[58vh] w-auto h-auto object-contain select-none"
              draggable="false"
              data-testid="portfolio-poster-logo"
            />
          ) : (
            <div
              className="text-center font-black tracking-[-0.04em] leading-[0.85] px-6 select-none"
              style={{
                color: item.logoInk || "rgba(255,255,255,0.85)",
                fontSize: "clamp(80px, 18vw, 280px)",
              }}
            >
              {(item.client || item.title).toUpperCase()}
            </div>
          )}
        </div>
      </section>

      {/* 2 · TITLE + LEDE (editorial pause) */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="k-overline text-[#FFC600] mb-4">Client · {item.client || item.title}</div>
            <h1
              data-testid="portfolio-detail-title"
              className="text-5xl sm:text-6xl lg:text-[104px] font-black tracking-[-0.03em] leading-[0.9]"
            >
              {item.title}.
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-white/85 text-lg lg:text-2xl leading-[1.4] max-w-xl">
              {item.summary}
            </p>
            <div className="mt-8 pt-8 border-t border-white/15">
              <div className="k-overline text-white/55 mb-3">Scope of work</div>
              <ul className="grid grid-cols-1 gap-1.5 text-sm text-white/85">
                {item.scope.map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <span aria-hidden className="text-[#FFC600] mt-1">◆</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {item.story ? (
        <>
          {/* 3 · THE PROBLEM — editorial pull spread */}
          <section data-testid="portfolio-story-problem" className="bg-black border-b border-white/10">
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-32">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-3">
                  <div className="k-overline text-[#FFC600] mb-3">01 · The problem</div>
                  <div className="hidden lg:block text-[128px] leading-[0.85] font-black text-white/[0.06] select-none">01</div>
                </div>
                <div className="lg:col-span-9">
                  <p className="text-2xl sm:text-3xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.18] text-white">
                    {item.story.problem}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4 · YELLOW INTERMISSION — big statement */}
          <section aria-hidden className="bg-[#FFC600] text-black">
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-14 flex items-baseline justify-between gap-6 flex-wrap">
              <div className="text-[11px] uppercase tracking-[0.32em] font-black">The sequence</div>
              <div className="text-[11px] uppercase tracking-[0.32em] font-black">
                {item.story.sequence.length} steps · {item.type}
              </div>
            </div>
          </section>

          {/* 5 · THE SEQUENCE — alternating numbered slabs */}
          <section data-testid="portfolio-story-sequence" className="bg-black border-b border-white/10">
            <div className="max-w-[1440px] mx-auto">
              {item.story.sequence.map((step, idx) => {
                const isEven = idx % 2 === 1;
                return (
                  <div
                    key={step}
                    className={`px-5 lg:px-10 py-14 lg:py-24 border-b border-white/10 last:border-b-0 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${
                      isEven ? "" : ""
                    }`}
                  >
                    <div className={`lg:col-span-4 ${isEven ? "lg:order-2 lg:text-right" : ""}`}>
                      <div
                        className="font-black leading-[0.82] tracking-[-0.05em]"
                        style={{
                          color: idx === item.story.sequence.length - 1 ? "#FFC600" : "rgba(255,255,255,0.9)",
                          fontSize: "clamp(96px, 14vw, 220px)",
                        }}
                      >
                        0{idx + 1}
                      </div>
                      <div className={`mt-2 k-overline text-white/55 ${isEven ? "lg:justify-end lg:flex" : ""}`}>
                        Step {idx + 1} of {item.story.sequence.length}
                      </div>
                    </div>
                    <div className={`lg:col-span-8 ${isEven ? "lg:order-1" : ""}`}>
                      <p className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.02em] leading-[1.22] text-white max-w-3xl">
                        {step}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 6 · SECONDARY VISUAL PLATE (brand plate reprise) */}
          <section aria-hidden className="relative overflow-hidden border-b border-white/10" style={{ background: brandBg }}>
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 lg:py-40 flex items-center justify-center">
              {hasLogo ? (
                <img
                  src={item.logo}
                  alt=""
                  className="max-w-[42%] max-h-[36vh] w-auto h-auto object-contain opacity-90 select-none"
                  draggable="false"
                />
              ) : (
                <div
                  className="font-black tracking-[-0.05em] leading-[0.85] text-center opacity-90"
                  style={{
                    color: item.logoInk || "rgba(255,255,255,0.85)",
                    fontSize: "clamp(64px, 12vw, 200px)",
                  }}
                >
                  {(item.client || item.title).toUpperCase()}
                </div>
              )}
            </div>
          </section>

          {/* 7 · THE RESULT — closing pull spread */}
          <section data-testid="portfolio-story-result" className="bg-black border-b border-white/10">
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-32">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-3">
                  <div className="k-overline text-[#FFC600] mb-3">03 · The result</div>
                  <div className="hidden lg:block text-[128px] leading-[0.85] font-black text-white/[0.06] select-none">03</div>
                </div>
                <div className="lg:col-span-9">
                  <p className="text-2xl sm:text-3xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.18] text-white">
                    {item.story.result}
                  </p>
                  <p className="mt-8 text-xs text-white/50 max-w-2xl leading-relaxed">
                    Kostaja does not publish invented metrics. Results are described by what changed in the operator's day and in the buyer's experience, not by numbers manufactured for the case study.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : null}

      {/* 8 · SERVICES USED */}
      {services.length ? (
        <section className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <Overline color="yellow" className="mb-3">Services used</Overline>
                <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">The working pieces behind this project.</h2>
              </div>
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="block bg-black border border-white/15 hover:border-[#FFC600] p-5 transition-colors">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC600] font-bold">Service</div>
                      <div className="text-lg font-black tracking-tighter mt-1 text-white">{s.name}</div>
                      <div className="mt-2 text-sm text-white/70 line-clamp-3">{s.outcome}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <RelatedContent
        serviceSlugs={item.relatedServices || []}
        industrySlugs={item.relatedIndustries || []}
        heading="Adjacent paths"
      />

      <CTABlock variant="portfolio" />
    </main>
  );
};
